import PropTypes from 'prop-types';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useIntl } from '@zenbusiness/application-commons-intl';
import Link from '@zenbusiness/zui/Link';
import ListItem from '@zenbusiness/zui/ListItem';
import SkeletonGrid from '@zenbusiness/zui/SkeletonGrid';
import Stack from '@zenbusiness/zui/Stack';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import * as Styled from './ProductCards.styles';

// Business License product card title override —
// With the release of an additional business license product (BLF), we need the card
// title to be more generic to reflect there are now multiple products.
const PRODUCT_TITLE_OVERRIDES = {
  'product.name.business_license_report': 'product.name.business_license_report.overrides'
};

function ProductCards({ items, loading }) {
  const { formatMessage: fm } = useIntl();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { businessEntityUuid } = useBusinessEntity();
  if (loading) {
    return <SkeletonGrid columns={isDesktop ? 3 : 2} count={isDesktop ? 9 : 6} />;
  }

  if (!items.length) {
    return null;
  }
  return (
    <Styled.ListCard>
      {items.map(({
        titleKey,
        descriptionKey,
        imagePath,
        primaryExternal,
        primaryPath
      }) => {
        const title = fm({ id: PRODUCT_TITLE_OVERRIDES[titleKey] || titleKey });
        const description = fm({ id: descriptionKey });

        return (
          <ListItem key={titleKey} sx={{ padding: 0 }}>
            <Styled.ProductCard
              aria-label={title}
              clickstreamProps={{
                elementName: 'product_card',
                elementPosition: 'products_page',
                elementValue: title,
                destination: primaryPath.replace('{BUUID}', businessEntityUuid) || primaryExternal
              }}
              component={Link}
              title={title}
              sx={{ height: '100%' }}
              size="xl"
              {...(primaryExternal && { target: '_blank', href: primaryExternal })}
              {...(primaryPath && { to: primaryPath.replace('{BUUID}', businessEntityUuid) })}
              variant="elevation"
            >
              <Stack alignItems="left" spacing={2}>
                <Styled.CardImageContainer>
                  <Styled.CardImage alt="" src={imagePath} />
                </Styled.CardImageContainer>
                <Stack gap={0.5}>
                  <Text size="sm" color="primary" weight="medium">
                    {title}
                  </Text>
                  <Text size="xs" color="secondary" weight="regular">
                    {description}
                  </Text>
                </Stack>
              </Stack>
            </Styled.ProductCard>
          </ListItem>
        );
      })}
    </Styled.ListCard>
  );
}

ProductCards.propTypes = {
  loading: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      titleKey: PropTypes.string,
      descriptionKey: PropTypes.string,
      imagePath: PropTypes.string,
      primaryExternal: PropTypes.string,
      primaryPath: PropTypes.string
    })
  ).isRequired
};

export default ProductCards;
